export const MYTASSEL_PLUS_ENTITLEMENTS_COLLECTION = "myTasselPlusEntitlements";
export const MYTASSEL_PLUS_CONFIG_DOC = {
  collection: "config",
  id: "myTasselPlus",
};

export const MYTASSEL_PLUS_FEATURES = {
  CUSTOM_BACKGROUND: "customBackground",
  CUSTOM_COLORS: "customColors",
  GOAL_IMAGE: "goalImage",
  PERSONALIZED_URL: "personalizedUrl",
  EVENT_IMAGES: "eventImages",
  POLAROID_IMAGES: "polaroidImages",
  SLIDESHOW_IMAGES: "slideshowImages",
};

export const MYTASSEL_PLUS_TIERS = {
  STANDARD: "standard",
  PLUS: "plus",
};

export const MYTASSEL_PLUS_LIMITS = {
  standard: {
    polaroidImages: 3,
    slideshowImages: 5,
  },
  plus: {
    // Product decision pending: 6 vs unlimited.
    // null means unlimited for now and can be changed in this one place.
    polaroidImages: null,
    // Product decision pending: 20 vs unlimited.
    // null means unlimited for now and can be changed in this one place.
    slideshowImages: null,
  },
};

export const MYTASSEL_PLUS_CAPABILITIES = {
  standard: {
    customBackground: false,
    customColors: false,
    goalImage: false,
    personalizedUrl: false,
    eventImages: false,
  },
  plus: {
    customBackground: true,
    customColors: true,
    goalImage: true,
    personalizedUrl: true,
    eventImages: true,
  },
};

export const MYTASSEL_PLUS_STANDARD_OPTIONS = {
  // TODO(product): choose the canonical 5 Standard backgrounds.
  // Events and Registry currently expose different predefined assets.
  backgrounds: null,
  // TODO(product): choose the canonical 6 Standard font colors.
  // Events currently exposes more than six preset colors.
  fontColors: null,
};

export const MYTASSEL_PLUS_BENEFITS = [
  "Custom uploaded background",
  "Custom colors",
  "Image on goal",
  "Personalized sharing URL",
  "Higher Polaroid allowance",
  "Higher Slideshow allowance",
  "Images on events",
];

export const MYTASSEL_PLUS_RESERVED_SLUGS = new Set([
  "",
  "admin",
  "api",
  "blog",
  "catalogue",
  "checklist",
  "contact-us",
  "dashboard",
  "events",
  "hub",
  "keychain",
  "privacy",
  "profile",
  "registry",
  "resources",
  "staging",
  "terms",
  "tutorials",
  "u",
  "unsubscribe",
  "verify-email",
  "why-mytassel",
  "whymytassel",
]);

export const normalizeMyTasselPlusSlug = (value = "") => (
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 48)
);

export const isReservedMyTasselPlusSlug = (slug) => {
  const normalized = normalizeMyTasselPlusSlug(slug);
  return MYTASSEL_PLUS_RESERVED_SLUGS.has(normalized) || /^g\d{5}$/i.test(normalized) || /^f\d{5}$/i.test(normalized);
};

export const getMyTasselPlusSlugCandidates = ({ firstName = "", lastName = "", uid = "" } = {}) => {
  const base = normalizeMyTasselPlusSlug(`${firstName} ${lastName}`) || normalizeMyTasselPlusSlug(firstName) || normalizeMyTasselPlusSlug(uid);
  if (!base || isReservedMyTasselPlusSlug(base)) return [];
  return [base, `${base}-2`, `${base}-3`, `${base}-${String(uid).toLowerCase()}`].filter(Boolean);
};

export const getMyTasselPlusAccess = ({ entitlement = null, rolloutConfig = null } = {}) => {
  const status = entitlement?.status;
  const source = entitlement?.source;
  const hasServerGrantedPlus = status === "active" || status === "founding" || status === "paid";

  if (hasServerGrantedPlus) {
    return {
      tier: MYTASSEL_PLUS_TIERS.PLUS,
      hasPlus: true,
      source: source || "server_entitlement",
      entitlement,
      reason: "server_granted",
    };
  }

  if (status === "expired" || status === "revoked" || status === "standard") {
    return {
      tier: MYTASSEL_PLUS_TIERS.STANDARD,
      hasPlus: false,
      source: source || "server_entitlement",
      entitlement,
      reason: status,
    };
  }

  const enforceStandard = rolloutConfig?.standardEnforcementEnabled === true;
  if (enforceStandard) {
    return {
      tier: MYTASSEL_PLUS_TIERS.STANDARD,
      hasPlus: false,
      source: "rollout_config",
      entitlement: null,
      reason: "standard_enforcement_enabled",
    };
  }

  return {
    tier: MYTASSEL_PLUS_TIERS.PLUS,
    hasPlus: true,
    source: "legacy_preservation",
    entitlement: null,
    reason: "standard_enforcement_not_enabled",
  };
};

export const canUseMyTasselPlusFeature = (access, feature) => {
  if (!feature) return true;
  if (access?.hasPlus) return true;
  return Boolean(MYTASSEL_PLUS_CAPABILITIES.standard[feature]);
};

export const getMyTasselPlusFeatureLimit = (access, feature) => {
  const tier = access?.hasPlus ? MYTASSEL_PLUS_TIERS.PLUS : MYTASSEL_PLUS_TIERS.STANDARD;
  return MYTASSEL_PLUS_LIMITS[tier][feature] ?? null;
};

export const canAddMyTasselPlusItems = (access, feature, currentCount = 0, addCount = 1) => {
  const limit = getMyTasselPlusFeatureLimit(access, feature);
  if (limit === null || limit === undefined) return true;
  return Number(currentCount) + Number(addCount) <= limit;
};

export const getMyTasselPlusFeatureLabel = (feature) => {
  const labels = {
    [MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND]: "custom backgrounds",
    [MYTASSEL_PLUS_FEATURES.CUSTOM_COLORS]: "custom colors",
    [MYTASSEL_PLUS_FEATURES.GOAL_IMAGE]: "goal images",
    [MYTASSEL_PLUS_FEATURES.PERSONALIZED_URL]: "personalized URLs",
    [MYTASSEL_PLUS_FEATURES.EVENT_IMAGES]: "event images",
    [MYTASSEL_PLUS_FEATURES.POLAROID_IMAGES]: "more Polaroids",
    [MYTASSEL_PLUS_FEATURES.SLIDESHOW_IMAGES]: "more Slideshow images",
  };
  return labels[feature] || "this option";
};
